/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import SelectDropdownExercisePicker from './SelectDropdownExercisePicker';
import MobileAddExercisePicker from './MobileAddExercisePicker';
import Text from '../../../../shared/components/Text/Text';
import useIsMobile from '../../../../shared/hooks/useIsMobile';

const ExerciseSelectorContainer = styled('div')`
	display: flex;
  width: 100%;
  /* TV mode: the add row is a coach affordance, not something athletes read,
   * so it shrinks to stay out of the way of the exercise list. */
  margin-top: ${(props) => (props.isTvMode ? '4px' : '10px')};
  margin-bottom: ${(props) => (props.isTvMode ? '8px' : '20px')};
  padding-left: ${(props) => props.addPaddingLeft && '25px'};
`;

/* One focusable row holding the "+" chip and its label, so the keyboard-focus
   ring wraps the whole "Add Exercise" affordance. The row's indent lives here
   (not on BorderContainer) so the ring hugs the content rather than the gap. */
const AddExerciseButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 5px;
  padding-right: 6px;
  border-radius: 4px;
  margin-left: ${(props) => {
    if (props.isTvMode) return props.isWeekView ? '10px' : '2px';
    if (!props.calendarItemsHaveLength && props.viewType === 'dayView' && !props.hasAfter && !props.hasBefore && props.programType === 'free-form-program') return '0';
    return props.isWeekView ? '10px' : '28px';
  }};

  /* global.scss forces outline: 0 !important on focused divs (see
     shared/theme/focusStyles.js), so the ring is a box-shadow, matching the
     house focusRing treatment. */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.hover};
  }
`;

const BorderContainer = styled('div')`
  position: relative;
  padding-right: 13px;
  align-items: center;
  display: flex;
  &::before {
    content: '';
    position: absolute;
    right: 1px;
    top: 50%;
    transform: translateY(-50%);
    height: 21px; /* Set the height of the border here */
    width: 2px;
    background-color: ${({ theme }) => theme.colors.text}; /* Border color */
  }
`;

/* TV mode sizes the chip to match the exercise rows' icon chips and fills it
 * with the theme's standard ink (black on light, white on dark) rather than
 * the CTA grey, so it reads from across the room in either appearance. */
const ButtonIcon = styled('div')`
  padding: 4px;
  background: ${({ theme, isTvMode }) => (isTvMode ? theme.colors.textStrong : theme.colors.buttonPrimaryBg)};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${(props) => (props.isTvMode ? '35px' : '25px')};
  min-width: ${(props) => (props.isTvMode ? '35px' : '25px')};
  max-width: ${(props) => (props.isTvMode ? '35px' : '25px')};
  max-height: ${(props) => (props.isTvMode ? '35px' : '25px')};
`;

const ExercisePicker = ({
  isWeekView, calendarItemsHaveLength, hasBefore, hasAfter, date, expandDay, dayNumber,
}) => {
  const [addExerciseToggleMenuOpen, setAddExerciseToggleMenuOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  /** phones use a dedicated full-screen picker instead of the react-select
   * dropdown, whose portaled popup covers the input / floats off-screen */
  const isMobile = useIsMobile();
  const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = isWeekView ? date : selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');

  const currentDateOrDayNumber = programType === 'free-form-program' ? dayNumber : endpointFormattedDate;
  const isThisDayHorizontallyExpanded = useSelector(
    (state) => String(state.calendar.data.weekViewExpandedDay) === String(currentDateOrDayNumber),
  );
  const { colors } = useTheme();

  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

  // Use a high tabIndex to ensure ExercisePicker comes after all exercise inputs and AddSet fields
  const tabIndex = 9999;

  /* The shortcut hint only fits where the row is full width: day view and an
     expanded week column. */
  const showShortcutHint = viewType === 'dayView' || (viewType === 'weekView' && isThisDayHorizontallyExpanded);
  const shortcutLabel = isMac ? 'Add Exercise (Ctrl+N)' : 'Add Exercise (Alt+N)';

  const handleClick = () => {
    if (isMobile) {
      setMobileOverlayOpen(true);
      return;
    }
    if (isWeekView && expandDay && !isThisDayHorizontallyExpanded) {
      expandDay();
      setTimeout(() => {
        setAddExerciseToggleMenuOpen(!addExerciseToggleMenuOpen);
      }, 600);
    } else {
      setAddExerciseToggleMenuOpen(!addExerciseToggleMenuOpen);
    }
  };

  /* Mouse: open on mousedown (so a press while the menu is open closes it
     before the search input's blur would), and keep the browser from moving
     focus onto the row — that would blur the freshly focused search input and
     close the menu again. */
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleClick();
  };

  /* Tabbing onto the button only highlights it (the focus ring); the picker
     opens on Enter/Space so keyboard users can pass over it without it firing. */
  const handleButtonKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    handleClick();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isWeekView && !isThisDayHorizontallyExpanded) return;

      const tag = e.target.tagName;
      if ((tag === 'INPUT' && e.target.type !== 'checkbox')
        || tag === 'TEXTAREA'
        || e.target.isContentEditable) {
        return;
      }

      const isCtrlN = isMac && e.ctrlKey && e.key.toLowerCase() === 'n';
      const isAltN = !isMac && e.altKey && e.key.toLowerCase() === 'n';
      const isShiftSpace = e.shiftKey && e.key === ' ';

      if (isCtrlN || isAltN || isShiftSpace) {
        e.preventDefault();
        if (isMobile) {
          setMobileOverlayOpen(true);
        } else {
          setAddExerciseToggleMenuOpen(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isThisDayHorizontallyExpanded, isWeekView, isMac, isMobile]);

  return (
    <ExerciseSelectorContainer
      hasAfter={hasAfter}
      isTvMode={isTvMode}
      addPaddingLeft={((isWeekView
      && isThisDayHorizontallyExpanded))}
    >
      <AddExerciseButtonContainer
        role='button'
        aria-label='Add Exercise'
        data-add-exercise-button
        onMouseDown={handleMouseDown}
        onKeyDown={handleButtonKeyDown}
        tabIndex={tabIndex}
        isWeekView={isWeekView}
        calendarItemsHaveLength={calendarItemsHaveLength}
        viewType={viewType}
        hasAfter={hasAfter}
        hasBefore={hasBefore}
        programType={programType}
        isTvMode={isTvMode}
      >
        <BorderContainer>
          <ButtonIcon isTvMode={isTvMode}>
            <IcomoonReact
              iconSet={iconSet}
              size={isTvMode ? 20 : 12}
              icon='plus'
              color={isTvMode ? colors.surface : colors.white}
            />
          </ButtonIcon>
        </BorderContainer>
        {/* The label gives way to the search input while the picker is open. */}
        {!addExerciseToggleMenuOpen && (
          <Text
            fontSize={isTvMode ? '14px' : '12pt'}
            style={{
              color: isTvMode ? colors.textSecondary : colors.text,
              whiteSpace: 'nowrap',
              marginLeft: 2,
              marginRight: 2,
            }}
          >
            {showShortcutHint ? shortcutLabel : 'Add Exercise'}
          </Text>
        )}
      </AddExerciseButtonContainer>
      {/* Both pickers mount only while open: each holds the account's full
          exercise list, and week view renders one ExercisePicker per day. */}
      {addExerciseToggleMenuOpen && (
        <SelectDropdownExercisePicker
          fullWidthInput
          placeholder='Search Exercises'
          addExercisePicker
          addExerciseToggleMenuOpen={addExerciseToggleMenuOpen}
          setAddExerciseToggleMenuOpen={setAddExerciseToggleMenuOpen}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          tabIndex={-1}
          date={date}
          dayNumber={dayNumber}
          isWeekView={isWeekView}
          expandDay={expandDay}
        />
      )}
      {mobileOverlayOpen && (
        <MobileAddExercisePicker
          isOpen
          onClose={() => setMobileOverlayOpen(false)}
          date={date}
          dayNumber={dayNumber}
        />
      )}
    </ExerciseSelectorContainer>
  );
};

ExercisePicker.defaultProps = {
  isWeekView: false,
  calendarItemsHaveLength: false,
  hasBefore: false,
  hasAfter: false,
  date: null,
  dayNumber: null,
  expandDay: () => {},
};

ExercisePicker.propTypes = {
  isWeekView: PropTypes.bool,
  calendarItemsHaveLength: PropTypes.bool,
  hasBefore: PropTypes.bool,
  hasAfter: PropTypes.bool,
  date: PropTypes.string,
  dayNumber: PropTypes.number,
  expandDay: PropTypes.func,
};

export default ExercisePicker;
